import { USER_ROLE } from "Constants/constant";

export const PLATFORM_TENANT_CONTEXT_KEY = "platformTenantContext";
export const GHOST_SESSION_KEY = "ghostSession";
export const PLATFORM_TENANT_CHROME_HEIGHT_PX = 49;
export const PLATFORM_GHOST_BANNER_HEIGHT_PX = 49;
export const PLATFORM_ACCENT_HEIGHT_PX = 4;
export const PLATFORM_ADMIN_CHROME_BODY_CLASS = "platform-admin-chrome-active";

export const computePlatformAdminChromeHeight = ({
  showAccent = false,
  showGhostBanner = false,
  showTenantBar = false,
} = {}) => {
  let height = 0;

  if (showAccent) {
    height += PLATFORM_ACCENT_HEIGHT_PX;
  }
  if (showGhostBanner) {
    height += PLATFORM_GHOST_BANNER_HEIGHT_PX;
  }
  if (showTenantBar) {
    height += PLATFORM_TENANT_CHROME_HEIGHT_PX;
  }

  return height;
};

export const readPlatformTenantContext = () => {
  const raw = localStorage.getItem(PLATFORM_TENANT_CONTEXT_KEY);
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw);
    if (parsed?.spId != null && parsed?.spName) {
      return {
        spId: parsed.spId,
        spName: parsed.spName,
        returnTo: parsed.returnTo || null,
      };
    }
  } catch (e) {
    return null;
  }

  return null;
};

export const writePlatformTenantContext = (context) => {
  if (!context?.spId || !context?.spName) {
    localStorage.removeItem(PLATFORM_TENANT_CONTEXT_KEY);
    return;
  }

  localStorage.setItem(
    PLATFORM_TENANT_CONTEXT_KEY,
    JSON.stringify({
      spId: context.spId,
      spName: context.spName,
      ...(context.returnTo ? { returnTo: context.returnTo } : {}),
    })
  );
};

export const readGhostSession = () => {
  const raw = localStorage.getItem(GHOST_SESSION_KEY);
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw);
    if (parsed?.token) {
      return parsed;
    }
  } catch (e) {
    return null;
  }

  return null;
};

export const writeGhostSession = (session) => {
  if (!session?.token) {
    localStorage.removeItem(GHOST_SESSION_KEY);
    return;
  }

  localStorage.setItem(GHOST_SESSION_KEY, JSON.stringify(session));
};

export const clearGhostSession = () => {
  localStorage.removeItem(GHOST_SESSION_KEY);
};

export const isGhostSessionActive = () =>
  isSuperAdminUser() && Boolean(readGhostSession()?.token);

/** Platform staff (user_type super_admin) — not sp_admin or tenant Super Admin manager role. */
export const isSuperAdminUser = () =>
  localStorage.getItem("userType") === USER_ROLE.Super_Admin;

export const isPlatformTenantContextActive = () =>
  isSuperAdminUser() && readPlatformTenantContext()?.spId != null;

/** Managers: login clears selectedProvider; send sp_id from session user for API tenancy. */
export const resolveSpIdFromStoredUser = () => {
  const cachedUser = localStorage.getItem("user");
  if (!cachedUser) {
    return undefined;
  }

  try {
    const user = JSON.parse(cachedUser);
    return (
      user?.sp_id ??
      user?.service_provider?.id ??
      user?.payment_setting?.sp_id ??
      undefined
    );
  } catch (e) {
    return undefined;
  }
};

export const resolveSelectedProviderSpId = () => {
  const cachedProvider = localStorage.getItem("selectedProvider");
  if (!cachedProvider) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(cachedProvider);
    return parsed?.value;
  } catch (e) {
    return undefined;
  }
};

/**
 * Attach tenant-scope headers for platform admin tenant context and ghost sessions.
 * Never mutates localStorage user identity keys — only request headers.
 */
export const applyTenantScopeHeaders = (headers = {}) => {
  const tenantContext = readPlatformTenantContext();
  const ghostSession = readGhostSession();
  const ghostSpId = ghostSession?.spId;
  const usingPlatformTenantContext =
    isSuperAdminUser() && tenantContext?.spId != null;
  const usingGhostTenantScope =
    isSuperAdminUser() && !usingPlatformTenantContext && ghostSpId != null;

  if (isSuperAdminUser()) {
    if (tenantContext?.spId != null) {
      headers["x-sp-id"] = String(tenantContext.spId);
    } else if (ghostSpId != null) {
      headers["x-sp-id"] = String(ghostSpId);
    }

    if (ghostSession?.token) {
      headers["X-Ghost-Token"] = ghostSession.token;
    }
  }

  if (!usingPlatformTenantContext && !usingGhostTenantScope) {
    const spId =
      resolveSelectedProviderSpId() ?? resolveSpIdFromStoredUser();
    if (spId) {
      headers.sp_id = spId;
    }
  }

  return headers;
};
